import type { BadgeTone } from '@/ui/kit';
import type { SyncAttempt, SyncRecoveryRequest } from './syncSupervisionApi';

export const ATTEMPT_LABELS: Readonly<Record<SyncAttempt['status'], string>> = {
  accepted: 'Acceptée',
  conflict: 'Conflit métier',
  rejected: 'Refusée',
  technical_error: 'Erreur technique',
};
export const ATTEMPT_TONES: Readonly<Record<SyncAttempt['status'], BadgeTone>> = {
  accepted: 'success',
  conflict: 'warning',
  rejected: 'danger',
  technical_error: 'danger',
};
export const REQUEST_LABELS: Readonly<Record<SyncRecoveryRequest['status'], string>> = {
  pending: 'En attente de l’appareil',
  running: 'En cours',
  completed: 'Cycle terminé',
  failed: 'Échouée',
  expired: 'Expirée sans exécution confirmée',
};
const CAUSES: Readonly<Record<string, readonly [string, string]>> = {
  none: [
    'Aucune erreur signalée',
    'Cette tentative a été acceptée ; cela ne prouve pas que toutes les données de l’appareil sont à jour.',
  ],
  version_obsolete: [
    'Version devenue obsolète',
    'Le parent doit arbitrer dans l’application. Relancer ne force pas sa version.',
  ],
  forbidden: [
    'Permissions insuffisantes',
    'Vérifier l’appartenance au foyer et le rôle. Une relance ne rétablit pas les droits retirés.',
  ],
  validation_failed: [
    'Données refusées par la validation',
    'Vérifier la version de l’application et corriger la saisie ; ne pas modifier le journal.',
  ],
  insufficient_stock: [
    'Quantité de stock insuffisante',
    'Le lot a pu être utilisé ailleurs. Choisir un autre lot ou conserver le repas sans débit dans l’application.',
  ],
  stock_state_changed: [
    'État du lot modifié ailleurs',
    'Relire les données du serveur puis arbitrer dans l’application. Aucun débit n’est forcé.',
  ],
  batch_not_available: [
    'Lot indisponible',
    'Vérifier la traçabilité du lot ; une resynchronisation ne crée pas de stock.',
  ],
  idempotency_key_reused: [
    'Clé d’opération réutilisée',
    'Vérifier la version du mobile ; les opérations acceptées ne doivent jamais être rejouées sous une clé arbitraire.',
  ],
  rate_limited: [
    'Trop de requêtes',
    'Attendre la fin du quota et rechercher une boucle de synchronisation avant de relancer.',
  ],
  service_unavailable: [
    'Service indisponible',
    'Vérifier la santé du service et la maintenance, puis relancer après correction.',
  ],
  server_error: [
    'Erreur technique du serveur',
    'Consulter la santé du service et les journaux techniques expurgés avant de relancer.',
  ],
  authentication_failed: ['Session refusée', 'La personne doit se reconnecter sur son appareil.'],
  invitation_quota_exceeded: [
    'Quota d’invitations atteint',
    'Attendre ou vérifier les invitations existantes.',
  ],
  already_member: ['Personne déjà membre', 'Vérifier les membres du foyer avant une nouvelle invitation.'],
  self_invitation: ['Invitation de soi-même', 'Corriger le destinataire dans l’application.'],
};
export const causeOf = (reason: string): readonly [string, string] =>
  CAUSES[reason] ?? [
    'Cause non précisée',
    'Examiner le contexte ; ne pas considérer une relance comme un arbitrage métier.',
  ];
export const resultLabel = (code: string | null): string =>
  ({
    cycle_finished: 'Cycle exécuté ; aucune opération locale en attente signalée.',
    action_required:
      'Cycle exécuté ; des saisies ou conflits demandent encore une action dans l’application.',
    cycle_failed: 'Le cycle a échoué ; vérifier le réseau, la session et la version du mobile.',
    local_owner_mismatch: 'Les données locales appartiennent à un autre compte ; récupération refusée.',
    authentication_changed: 'La session a changé ; aucune récupération autorisée.',
    running: 'Exécution annoncée par l’appareil.',
  })[code ?? ''] ?? 'Aucun résultat reçu.';
export const instant = (value: string): string => {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('fr-FR') : '—';
};

/**
 * « 1 demande · page 1 » : le pluriel français commence à deux, zéro et un
 * restent au singulier (#657, « 1 demandes »).
 */
export function pagedCountLabel(total: number, singular: string, plural: string, page: number): string {
  return `${String(total)} ${total > 1 ? plural : singular} · page ${String(page)}`;
}
